// Constants
import { Key } from "@ms/uno-constants/lib/local/KeyCode";
// Utilities
import * as React from "react";
import forEachRight from "lodash/forEachRight";
import indexOf from "lodash/indexOf";
/** String used for data- attribute for Landmark */
export const LANDMARK_ATTR = "data-landmark";
/** Selector string for finding landmarks */
const LANDMARK_SELECTOR = `[${LANDMARK_ATTR}]`;
/**
 * A wrapper that looks for Landmarks to allow accessibile navigation of children.
 * Only one LandmarkWrapper should exist on the page at all times and should appear
 * above all potential Landmarks
 */
export const LandmarkWrapper = (props) => {
    /**
     * Focus a landmark and set all other landmarks to not be focusable
     * @param {HTMLElement} nextLandmark The landmark to focus
     */
    const focusLandmark = React.useCallback((nextLandmark) => {
        if (nextLandmark) {
            const allLandmarks = Array.prototype.slice.call(document.querySelectorAll(LANDMARK_SELECTOR));
            // Change all tab indexes to -1 for all landmarks except the one we want to focus
            for (const landmark of allLandmarks) {
                if (landmark === nextLandmark) {
                    landmark.tabIndex = 0;
                    landmark.focus();
                }
                else {
                    landmark.tabIndex = -1;
                }
            }
        }
    }, []);
    /**
     * Go to the landmark from a landmark
     * @param isSearchForward Whether we're going next or previous
     * @param currentLandmark The landmark we're currently moving from
     */
    const findNextLandmarkFromLandmark = React.useCallback((isSearchForward, currentLandmark) => {
        // Get all the landmarks on the page
        const allLandmarks = Array.prototype.slice.call(document.querySelectorAll(LANDMARK_SELECTOR));
        // Find the landmark we're checking from
        let currentIndex = indexOf(allLandmarks, currentLandmark);
        // Increase or decrease where we're going and ensure we wrap if necessary
        if (!isSearchForward) {
            currentIndex -= 1;
            if (currentIndex < 0) {
                currentIndex = allLandmarks.length - 1;
            }
        }
        else {
            currentIndex += 1;
            if (currentIndex >= allLandmarks.length) {
                currentIndex = 0;
            }
        }
        // Return landmark that needs to be focused
        return allLandmarks[currentIndex];
    }, []);
    /**
     * Get the correct landmark from the document body
     * @param {boolean} isSearchForward Whether we're going next or previous
     * @returns {HTMLElement} The element to focus next
     */
    const getLandmarkFromDocumentBody = React.useCallback((isSearchForward) => {
        // If we're specifying the body element, then no Landmarks exist in the direction we're going
        // Check if any landmarks exist and choose the first/last depending on the direction
        const allLandmarks = Array.prototype.slice.call(document.querySelectorAll(LANDMARK_SELECTOR));
        if (allLandmarks.length > 0) {
            if (isSearchForward) {
                // First
                return allLandmarks[0];
            }
            else {
                // Last
                return allLandmarks[allLandmarks.length - 1];
            }
        }
        return null; // No landmarks found
    }, []);
    /**
     * Get all landmarks that we'd potentially go to when at an unknown location in the tree
     * @param {HTMLElement} landmarkContainingElement The element that contains landmarks we potentially are interested in
     * @param {HTMLElement} previousElement The first element between landmarkContainingElement and activeElement
     * @param {boolean} isSearchForward Whether we're going next or previous
     * @returns {HTMLElement} The Element to go to from Unknown, can be null if none is found
     */
    const getLandmarkFromUnknown = React.useCallback((landmarkContainingElement, previousElement, isSearchForward) => {
        // Get the index of the previousElement under its parent (landmarkContainingElement)
        const currentFocusedElementIndex = Array.prototype.slice
            .call(previousElement?.parentNode?.children ?? {})
            .indexOf(previousElement);
        let potentialLandmark = null;
        // Now, go through all the parent's children, on the side of the tree we're interested in going to (isSearchForward),
        // and gather all landmarks on that side, but not including any in the currentFocusedElement(Index)
        if (isSearchForward) {
            for (const [iter, child] of Array.from(landmarkContainingElement.children).entries()) {
                // forEach(landmarkContainingElement.children, (child: HTMLElement, iter: number) => {
                if (iter > currentFocusedElementIndex) {
                    const potentialElement = child.querySelector(LANDMARK_SELECTOR);
                    if (potentialElement) {
                        // When searching forward, we always take the first landmark we find
                        potentialLandmark = potentialElement;
                    }
                }
            }
        }
        else {
            forEachRight(landmarkContainingElement.children, (child, iter) => {
                if (iter < currentFocusedElementIndex) {
                    if (child.getAttribute(LANDMARK_ATTR)) {
                        // When moving previous, we want to include the child itself as that special case is not handled explicity
                        //potentialLandMarks.push(child);
                        potentialLandmark = child;
                        return false;
                    }
                    const potentialLandmarks = Array.prototype.slice.call(child.querySelectorAll(LANDMARK_SELECTOR));
                    if (potentialLandmarks.length > 0) {
                        potentialLandmark = potentialLandmarks[potentialLandmarks.length - 1];
                        return false;
                    }
                }
            });
        }
        return potentialLandmark;
    }, []);
    /**
     * Used to find a Landmark from a point that has no easy to access Landmarks
     * @param isSearchForward Whether we're going next or previous
     * @param {HTMLElement} element Optional param to say where we should check for parent landmarks
     * @returns {HTMLElement}  The next Landmark from the current position
     */
    const findNextLandmarkFromUnknown = React.useCallback((isSearchForward, element, previousElement) => {
        if (element === document.body) {
            return getLandmarkFromDocumentBody(isSearchForward);
        }
        // Find the parent that contains a landmark in its children and the child of that element the focusedElement is under (previousElement)
        let landmarkContainingElement = element;
        while (!landmarkContainingElement.querySelector(LANDMARK_SELECTOR)) {
            previousElement = landmarkContainingElement;
            if (landmarkContainingElement.getAttribute(LANDMARK_ATTR)) {
                // If a parent is a landmark, we want to go to the next one appropriately
                if (isSearchForward) {
                    return findNextLandmarkFromLandmark(isSearchForward, landmarkContainingElement);
                }
                else {
                    // If going previous, then we want to focus the parent
                    return landmarkContainingElement;
                }
            }
            if (landmarkContainingElement.parentElement) {
                landmarkContainingElement = landmarkContainingElement.parentElement;
            }
            else {
                // If we run out of parent elements, we don't have a Landmark to go to
                return null;
            }
        }
        // Now find the next landmark by finding the elements next to the current focused element.
        const potentialLandmark = getLandmarkFromUnknown(landmarkContainingElement, previousElement, isSearchForward);
        if (!potentialLandmark) {
            // If we don't find the next landmark, then we need to try the parentElement of the current landmarkContainingElement if it has one
            if (landmarkContainingElement.parentElement) {
                return findNextLandmarkFromUnknown(isSearchForward, landmarkContainingElement.parentElement, landmarkContainingElement);
            }
        }
        return potentialLandmark;
    }, [findNextLandmarkFromLandmark, getLandmarkFromDocumentBody, getLandmarkFromUnknown]);
    /**
     * Called when key down event is triggered. Cares about CTRL+F6 and SHIFT+CTRL+F6
     * @param event The event causing the key down.
     */
    const onKeyDown = React.useCallback((event) => {
        if (event.ctrlKey && event.key === Key.F6) {
            event.preventDefault();
            event.stopPropagation();
            const isSearchForward = !event.shiftKey; // True for next, false for previous
            let nextLandmark;
            if (document.activeElement?.getAttribute(LANDMARK_ATTR)) {
                // Case 1: Currently a landmark, so just go to the next/previous Landmark on the page
                nextLandmark = findNextLandmarkFromLandmark(isSearchForward, document.activeElement);
            }
            else if (isSearchForward && document.activeElement && document.activeElement.querySelector(LANDMARK_SELECTOR)) {
                // Case 2: Going next and the Landmark is under the current active element and we're going next
                nextLandmark = document.activeElement.querySelector(LANDMARK_SELECTOR);
            }
            else {
                // Case 3: Used if you're under a Landmark
                // Case 4: You're outside of all Landmarks but under the LandmarkWrapper
                nextLandmark = findNextLandmarkFromUnknown(isSearchForward, document.activeElement);
            }
            focusLandmark(nextLandmark);
        }
    }, [findNextLandmarkFromLandmark, findNextLandmarkFromUnknown, focusLandmark]);
    return React.createElement("div", { onKeyDown: onKeyDown }, props.children);
};
